import { useCallback, useEffect, useState } from 'react'

import { useToasts } from '@/hooks/useToasts'

import { Box, Text, Textarea } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'
import moment from 'moment'

import { DocumentNote, DocumentText, Note } from '@/components/icons'
import { Button, Spinner } from '@/components/ui'
import {
  useCreateDeviceNoteMutation,
  useDeviceLogItemsQuery,
} from '@/graphql/generated/hooks'
import { DeviceLogItemType } from '@/graphql/generated/schemas'
import { BUTTON_PRESS, TAB_PRESS, trackEvent } from '@/utils/analytics'
import { roleName } from '@/utils/roles'

// Centralized color palette for consistent theming
const COLORS = {
  ADD_NOTE: {
    BACKGROUND: '#E8F1FE',
    ACCENT: '#3D7FCE',
  },
  NOTE: {
    BACKGROUND: '#F6EDFF',
    ACCENT: '#5600CA',
  },
  CONFIG: {
    BACKGROUND: '#E8F1FE',
    ACCENT: '#101010',
  },
} as const

// Centralized styling configuration for different log types
const LOG_TYPE_CONFIG = {
  [DeviceLogItemType.Note]: {
    iconBgColor: COLORS.NOTE.BACKGROUND,
    iconColor: COLORS.NOTE.ACCENT,
    badgeBgColor: COLORS.NOTE.BACKGROUND,
    badgeTextColor: COLORS.NOTE.ACCENT,
    icon: DocumentNote,
    label: 'User Note',
    actionText: 'added a note',
  },
  [DeviceLogItemType.ConfigurationChange]: {
    iconBgColor: COLORS.CONFIG.BACKGROUND,
    iconColor: COLORS.CONFIG.ACCENT,
    badgeBgColor: COLORS.CONFIG.BACKGROUND,
    badgeTextColor: COLORS.CONFIG.ACCENT,
    icon: DocumentText,
    label: 'Configuration Change',
    actionText: 'made a configuration change',
  },
} as const

const getLogTypeConfig = (type: DeviceLogItemType) => LOG_TYPE_CONFIG[type]

type DeviceLogItemData = {
  id: string
  type: DeviceLogItemType
  message: string
  title?: string | null
  createdAt: string
  user?: {
    firstName?: string | null
    lastName?: string | null
    roleName?: string | null
  } | null
}

interface DeviceLogItemIProps {
  logItem: DeviceLogItemData
}

interface AddNoteSectionIProps {
  isAddingNote: boolean
  noteText: string
  onNoteTextChange: (text: string) => void
  onShowForm: () => void
  onCancel: () => void
  onAddNote: () => void
  isLoading: boolean
}

interface DeviceLogsIProps {
  deviceId: string
}

const formatLogDateTime = (dateString: string) => {
  return moment(dateString).format('HH:mm:ss | MM/DD/YY')
}

/**
 * Individual log item component that displays device log entries
 */
const DeviceLogItem = ({ logItem }: DeviceLogItemIProps) => {
  const styleConfig = getLogTypeConfig(logItem.type)

  // Format user information
  const fullName =
    logItem.user && (logItem.user.firstName || logItem.user.lastName)
      ? `${logItem.user.firstName || ''} ${logItem.user.lastName || ''}`.trim()
      : 'Unknown User'
  const formattedRoleName = logItem.user?.roleName
    ? roleName(logItem.user.roleName)
    : 'User'

  // Generate the action title based on log type
  // For CONFIGURATION_CHANGE items, use the backend title if available. For NOTE items, build the title ourselves
  const title =
    logItem.type === DeviceLogItemType.ConfigurationChange && logItem.title
      ? logItem.title
      : `${formattedRoleName} ${fullName} ${styleConfig.actionText}`

  return (
    <Box
      bg='white'
      borderColor='border.200'
      borderRadius='10px'
      borderWidth='1px'
      mb='8px'
      px='16px'
      py='8px'
    >
      <Box alignItems='flex-start' display='flex' gap='12px'>
        {/* Icon on the left */}
        <Box
          alignItems='center'
          bg={styleConfig.iconBgColor}
          borderRadius='16px'
          color={styleConfig.iconColor}
          display='flex'
          justifyContent='center'
          p='8px 8px'
        >
          <styleConfig.icon />
        </Box>

        {/* Content */}
        <Box flex='1'>
          {/* Title and timestamp row */}
          <Box
            alignItems='center'
            display='flex'
            justifyContent='space-between'
            mb='4px'
          >
            <Text as='span' color='gray.700' fontSize='16px' fontWeight='500'>
              {title}
            </Text>
            <Text color='gray.600' fontSize='12px' fontWeight='400'>
              {formatLogDateTime(logItem.createdAt)}
            </Text>
          </Box>

          {/* Badge for the log type */}
          <Box alignItems='center' display='flex' mb='4px'>
            <Box
              as='span'
              bg={styleConfig.badgeBgColor}
              borderRadius='8px'
              color={styleConfig.badgeTextColor}
              fontSize='10px'
              fontWeight='100'
              px='6px'
            >
              {styleConfig.label}
            </Box>
          </Box>

          {/* Log message */}
          <Text as='span' color='gray.700' fontSize='sm'>
            {logItem.message}
          </Text>
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Component for adding a new device note
 */
const AddNoteSection = ({
  isAddingNote,
  noteText,
  onNoteTextChange,
  onShowForm,
  onCancel,
  onAddNote,
  isLoading,
}: AddNoteSectionIProps) => {
  return (
    <Box mb={4}>
      <AnimatePresence>
        {isAddingNote ? (
          <motion.div
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            initial={{ opacity: 0, y: -10, height: 0 }}
            key='form'
            style={{ overflow: 'hidden' }}
            transition={{
              duration: 0.3,
              ease: 'easeInOut',
              height: { duration: 0.2 },
            }}
          >
            {/* Textarea */}
            <Textarea
              mb={4}
              minH='80px'
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                onNoteTextChange(e.target.value)
              }
              placeholder='Write your note here...'
              resize='vertical'
              value={noteText}
            />

            {/* Buttons */}
            <Box display='flex' gap={2} justifyContent='flex-end'>
              <Button onClick={onCancel} type='button' variant='secondary'>
                Cancel
              </Button>
              <Button
                isDisabled={!noteText.trim() || isLoading}
                isLoading={isLoading}
                loadingText='Submitting...'
                onClick={onAddNote}
                type='button'
              >
                Add Note
              </Button>
            </Box>
          </motion.div>
        ) : (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            initial={{ opacity: 0, y: 10 }}
            key='header'
            transition={{ duration: 0.05, ease: 'easeInOut' }}
          >
            {/* Section header with pencil icon, clickable to show form */}
            <Box
              _hover={{ opacity: 0.8 }}
              cursor='pointer'
              mb={6}
              mt={3}
              onClick={onShowForm}
            >
              <Box alignItems='center' display='flex'>
                <Box
                  alignItems='center'
                  bg={COLORS.ADD_NOTE.BACKGROUND}
                  borderRadius='6px'
                  color={COLORS.ADD_NOTE.ACCENT}
                  display='flex'
                  justifyContent='center'
                  mr={3}
                  p='6px 18px'
                  sx={{
                    '& svg': {
                      width: '16px',
                      height: '16px',
                    },
                  }}
                >
                  <Note strokeColor={COLORS.ADD_NOTE.ACCENT} />
                </Box>
                <Text as='span' color='gray.700' fontSize='14px'>
                  Add a note about this device…
                </Text>
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

/**
 * Main component for displaying device logs
 */
export const DeviceLogs = ({ deviceId }: DeviceLogsIProps) => {
  const [noteText, setNoteText] = useState('')
  const [isAddingNote, setIsAddingNote] = useState(false)
  const { showError } = useToasts()
  const [createDeviceNoteMutation, { loading: isCreatingNote }] =
    useCreateDeviceNoteMutation()

  useEffect(() => {
    trackEvent(`${TAB_PRESS} Device Drawer - Device Logs'`, { deviceId })
  }, [])

  const {
    data,
    loading: isLoading,
    error,
    refetch,
  } = useDeviceLogItemsQuery({
    variables: { deviceId },
    skip: !deviceId,
  })

  const handleShowNoteForm = useCallback(() => {
    setIsAddingNote(true)
  }, [])

  const handleCancelNote = useCallback(() => {
    setNoteText('')
    setIsAddingNote(false)
  }, [])

  const handleAddNote = useCallback(async () => {
    if (!noteText.trim()) return

    try {
      await createDeviceNoteMutation({
        variables: {
          input: {
            deviceId,
            message: noteText.trim(),
          },
        },
      })
      trackEvent(`${BUTTON_PRESS} Device Drawer - Device Logs > Add Note`, {
        deviceId,
      })
      setNoteText('')
      setIsAddingNote(false)
      refetch()
    } catch (error) {
      showError('Failed to add device note. Please try again.')
    }
  }, [noteText, deviceId, createDeviceNoteMutation, refetch, showError])

  if (isLoading) {
    return (
      <Box alignItems='center' display='flex' justifyContent='center' p='16px'>
        <Spinner />
      </Box>
    )
  }

  if (error) {
    return (
      <Box color='red.500' p='16px' textAlign='center'>
        Error loading device logs
      </Box>
    )
  }

  const logItems = (data?.deviceLogItems?.edges
    ?.map((edge) => edge?.node)
    .filter(Boolean) || []) as DeviceLogItemData[]

  return (
    <Box px={5} py={4}>
      {/* Add Note Section */}
      <AddNoteSection
        isAddingNote={isAddingNote}
        isLoading={isCreatingNote}
        noteText={noteText}
        onAddNote={handleAddNote}
        onCancel={handleCancelNote}
        onNoteTextChange={setNoteText}
        onShowForm={handleShowNoteForm}
      />

      {/* Divider */}
      <Box mb={4}>
        <Box borderBottom='1px solid' borderColor='gray.300' width='100%' />
      </Box>

      {/* Device Logs List */}
      {logItems.length === 0 ? (
        <Box color='gray.500' p='16px' textAlign='center'>
          No device logs found for this device.
        </Box>
      ) : (
        logItems.map((logItem) => (
          <DeviceLogItem key={logItem.id} logItem={logItem} />
        ))
      )}
    </Box>
  )
}
